import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import {
    AlertController,
    ModalController,
    NavParams,
    Platform,
    ToastController,
} from '@ionic/angular';
import { VoiceRecorder, RecordingData } from 'capacitor-voice-recorder';
import { AjaxService } from '../ajax.service';
import { I18nService } from '../shared/i18n.service';
import { GlobalService } from '../global.service';
import { Keyboard } from '@capacitor/keyboard';
import { PluginListenerHandle } from '@capacitor/core';

@Component({
    selector: 'app-voice-memo',
    templateUrl: './voice-memo.page.html',
    styleUrls: ['./voice-memo.page.scss'],
    standalone: false,
})
export class VoiceMemoPage implements OnInit, OnDestroy {
    isRecording = false;
    isPlaying = false;

    duration = 0;
    durationDisplay = '0:00';
    timerInterval: any;

    recordedAudio: RecordingData | null = null;

    private audioPlayer = new Audio(); // Use HTML5 Audio like in note-details
    audioProgress = 0;

    orderNumber: string;
    transcribedText: string = '';
    isTranscribing = false;
    keyboardOpen = false;
    private keyboardShowListener: PluginListenerHandle | null = null;
    private keyboardHideListener: PluginListenerHandle | null = null;

    constructor(
        private modalCtrl: ModalController,
        private zone: NgZone,
        private ajax: AjaxService,
        private alertController: AlertController,
        private navParams: NavParams,
        public i18n: I18nService,
        private global: GlobalService,
        private toastCtrl: ToastController,
        private platform: Platform
    ) {}

    async ngOnInit() {
        this.orderNumber = this.navParams.get('orderNumber');
        VoiceRecorder.requestAudioRecordingPermission();
        this.keyboardShowListener = await Keyboard.addListener(
            'keyboardWillShow',
            () => {
                this.zone.run(() => {
                    this.keyboardOpen = true;
                });
            }
        );

        this.keyboardHideListener = await Keyboard.addListener(
            'keyboardWillHide',
            () => {
                this.zone.run(() => {
                    this.keyboardOpen = false;
                });
            }
        );
    }

    ngOnDestroy() {
        if (this.audioPlayer) {
            this.audioPlayer.pause();
            this.audioPlayer.src = '';
        }
        clearInterval(this.timerInterval);
        if (this.keyboardShowListener) {
            this.keyboardShowListener.remove();
        }
        if (this.keyboardHideListener) {
            this.keyboardHideListener.remove();
        }
    }

    // ? after the screen is rendered -> user instanly records
    ionViewDidEnter() {
        this.startRecording();
    }

    // ? recording logic

    async startRecording() {
        if (this.isRecording) {
            return;
        }

        this.isRecording = true;
        this.recordedAudio = null;
        this.startTimer();

        await VoiceRecorder.startRecording();
    }

    // ? playback logic

    async prepareAudioForPlayback() {
        if (!this.recordedAudio?.value.recordDataBase64) return;

        // Create a blob URL for the audio
        const audioBlob = this.base64toBlob(
            this.recordedAudio.value.recordDataBase64,
            this.recordedAudio.value.mimeType
        );
        const audioUrl = URL.createObjectURL(audioBlob);

        this.audioPlayer.src = audioUrl;

        // Set up progress tracking (same as note-details)
        this.audioPlayer.ontimeupdate = () => {
            this.zone.run(() => {
                if (this.audioPlayer.duration) {
                    this.audioProgress =
                        this.audioPlayer.currentTime /
                        this.audioPlayer.duration;
                }
            });
        };

        this.audioPlayer.onended = () => {
            this.zone.run(() => {
                this.isPlaying = false;
                this.audioProgress = 0;
            });
        };
    }

    playAudio() {
        if (this.isPlaying) {
            this.audioPlayer.pause();
            this.isPlaying = false;
            return;
        }

        this.audioPlayer.play().catch((err) => {
            console.error('Error playing audio:', err);
            this.presentToast(
                this.i18n.tr('Fout bij afspelen audio'),
                'danger'
            );
        });
        this.isPlaying = true;
    }

    seekAudio(event: any) {
        const seekPosition = event.detail.value;
        if (this.audioPlayer.duration) {
            this.audioPlayer.currentTime =
                (seekPosition / 100) * this.audioPlayer.duration;
        }
        this.audioProgress = seekPosition / 100;
    }

    base64toBlob(base64Data: string, contentType = '', sliceSize = 512): Blob {
        const byteCharacters = atob(base64Data);
        const byteArrays = [];
        for (
            let offset = 0;
            offset < byteCharacters.length;
            offset += sliceSize
        ) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        return new Blob(byteArrays, { type: contentType });
    }

    // ? timer logic

    startTimer() {
        this.duration = 0;
        this.timerInterval = setInterval(() => {
            this.duration++;
            this.formatDuration();
        }, 1000);
    }

    stopTimer() {
        clearInterval(this.timerInterval);
    }

    formatDuration() {
        const minutes = Math.floor(this.duration / 60);
        const seconds = this.duration % 60;
        const paddedSeconds = seconds < 10 ? `0${seconds}` : seconds;
        this.durationDisplay = `${minutes}:${paddedSeconds}`;
    }

    // ?  modal controls

    cancel() {
        this.modalCtrl.dismiss(null, 'cancel');
    }

    async stopRecording() {
        if (!this.isRecording) {
            return;
        }

        this.isRecording = false;
        this.stopTimer();

        const result = await VoiceRecorder.stopRecording();
        this.recordedAudio = result;

        if (this.recordedAudio) {
            this.getTextFromServer(this.recordedAudio);
            this.prepareAudioForPlayback();
        }
    }

    getTextFromServer(audioData: RecordingData) {
        this.isTranscribing = true;
        this.transcribedText = '';

        const params = {
            // ? send the raw audio data as a Base64 string
            audioContent: audioData.value.recordDataBase64,
            sttcode: this.i18n.getSTTlanguage(this.global.language),
        };

        this.ajax
            .get('transcribeAudio', params)
            .then((response: any) => {
                if (response.success && response.text) {
                    this.transcribedText = response.text;
                } else {
                    this.presentToast(
                        this.i18n.tr('Error: Kon audio niet transcriberen'),
                        'danger'
                    );
                    console.error('Transcription Error:', response);
                }
            })
            .catch((err) => {
                this.presentToast(
                    this.i18n.tr('Error: API-verbinding mislukt.'),
                    'danger'
                );
                console.error('API Error:', err);
            })
            .finally(() => {
                this.isTranscribing = false;
            });
    }

    // * new one to upload audio
    saveAndClose() {
        this.modalCtrl.dismiss(
            {
                text: this.transcribedText,
                audio: this.recordedAudio,
            },
            'confirm'
        );
    }

    async presentToast(message: string, color: string) {
        const toast = await this.toastCtrl.create({
            message,
            duration: 3000,
            color,
            position: 'bottom',
        });
        toast.present();
    }
}
